/**
 * Sicheres, minimales Rich-Text-Format für "Routenregeln" (Fett/Unterstrichen/
 * normaler Text) - siehe backend/src/routes/routeRules.ts für den
 * Hintergrund. Gespeichert wird ein einfaches Klammer-Markup
 * ("[b]fett[/b]", "[u]unterstrichen[/u]", Zeilenumbrüche als "\n"), NIE
 * rohes HTML. Text-Sonderzeichen ("[", "]", "\") werden beim Serialisieren
 * mit "\" escaped und beim Parsen wieder entfernt, damit vom Benutzer
 * getippte eckige Klammern nicht versehentlich als Formatierung
 * interpretiert werden.
 *
 * Drei Verwendungen:
 *   - parseMarkup(): Markup -> Zeilen von Segmenten (Zwischenformat).
 *   - segmentsToReact(): Segmente -> echte React-Elemente (LesevAnsicht) -
 *     ausschließlich <strong>/<u>/Text, React escaped Textinhalte automatisch.
 *   - segmentsToSafeHtml(): Segmente -> HTML-STRING, aber ausschließlich aus
 *     einer festen Erlaubnisliste (<b>/<u>/<br>) mit manuell escapetem Text
 *     aufgebaut - wird NUR als initialer, einmaliger innerHTML-Wert des
 *     bearbeitbaren contentEditable-Elements verwendet (siehe
 *     RouteRulesPage.tsx), niemals mit rohem/ungeprüftem Text.
 *   - domToMarkup(): liest das (vom Browser während der Bearbeitung
 *     veränderte) contentEditable-DOM aus und baut daraus wieder das
 *     Markup - erkennt dabei ausschließlich b/strong/u/div/p/br, jedes
 *     andere Element (z.B. beim Einfügen von fremdem Inhalt) wird auf
 *     seinen reinen Textinhalt reduziert, nie als HTML übernommen.
 */
import { Fragment, type ReactNode } from "react";

export interface Segment {
  text: string;
  bold: boolean;
  underline: boolean;
}

function escapeMarkupText(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/\[/g, "\\[").replace(/\]/g, "\\]");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

/** Markup-String -> Zeilen von {text, bold, underline}-Segmenten. */
export function parseMarkup(markup: string): Segment[][] {
  const lines: Segment[][] = [[]];
  let bold = false;
  let underline = false;
  let i = 0;

  const push = (ch: string) => {
    const line = lines[lines.length - 1]!;
    const last = line[line.length - 1];
    if (last && last.bold === bold && last.underline === underline) last.text += ch;
    else line.push({ text: ch, bold, underline });
  };

  while (i < markup.length) {
    if (markup.startsWith("[b]", i)) { bold = true; i += 3; continue; }
    if (markup.startsWith("[/b]", i)) { bold = false; i += 4; continue; }
    if (markup.startsWith("[u]", i)) { underline = true; i += 3; continue; }
    if (markup.startsWith("[/u]", i)) { underline = false; i += 4; continue; }
    if (markup[i] === "\n") { lines.push([]); i += 1; continue; }
    if (markup[i] === "\\" && (markup[i + 1] === "[" || markup[i + 1] === "]" || markup[i + 1] === "\\")) {
      push(markup[i + 1]!); i += 2; continue;
    }
    push(markup[i]!); i += 1;
  }
  return lines;
}

/** Segmente -> echte React-Kindelemente für die Leseansicht (kein HTML-String). */
export function segmentsToReact(lines: Segment[][]): ReactNode {
  return lines.map((line, li) => (
    <Fragment key={li}>
      {li > 0 && <br />}
      {line.map((seg, si) => {
        let node: ReactNode = seg.text;
        if (seg.bold) node = <strong>{node}</strong>;
        if (seg.underline) node = <u>{node}</u>;
        return <Fragment key={si}>{node}</Fragment>;
      })}
    </Fragment>
  ));
}

/** Segmente -> HTML-String, ausschließlich aus <b>/<u>/<br> aufgebaut, aller
 *  Textinhalt manuell HTML-escaped - NUR für den initialen innerHTML-Wert
 *  des contentEditable-Feldes bestimmt (siehe Docstring oben). */
export function segmentsToSafeHtml(lines: Segment[][]): string {
  return lines
    .map((line) =>
      line
        .map((seg) => {
          let html = escapeHtml(seg.text);
          if (seg.bold) html = `<b>${html}</b>`;
          if (seg.underline) html = `<u>${html}</u>`;
          return html;
        })
        .join("") || "​" // leere Zeile braucht einen Platzhalter, sonst verschluckt der Browser sie
    )
    .join("<br>");
}

/** Liest das (ggf. vom Browser veränderte) contentEditable-DOM aus und baut
 *  daraus das sichere Markup - erkennt nur b/strong/u/div/p/br, alles
 *  andere wird auf reinen Text reduziert. */
export function domToMarkup(root: Node): string {
  let bold = false;
  let underline = false;
  let out = "";

  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      out += escapeMarkupText((node.textContent ?? "").replace(/​/g, ""));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const el = node as HTMLElement;
    const tag = el.tagName;

    if (tag === "BR") { out += "\n"; return; }

    const isBold = tag === "B" || tag === "STRONG";
    const isUnderline = tag === "U";
    const wasBold = bold, wasUnderline = underline;
    if (isBold) { out += "[b]"; bold = true; }
    if (isUnderline) { out += "[u]"; underline = true; }

    el.childNodes.forEach(walk);

    if (isUnderline) { out += "[/u]"; underline = wasUnderline; }
    if (isBold) { out += "[/b]"; bold = wasBold; }

    // Block-Elemente (DIV/P - wie sie contentEditable beim Drücken von
    // Enter erzeugt) beenden ihre Zeile, außer es ist der allerletzte Knoten.
    if ((tag === "DIV" || tag === "P") && el.nextSibling) out += "\n";
  }

  root.childNodes.forEach(walk);
  return out;
}

/** Prüft, ob geparstes Markup vollständig leer ist (nur leere Zeilen). */
export function isMarkupEmpty(lines: Segment[][]): boolean {
  return lines.every((line) => line.every((seg) => seg.text.trim() === ""));
}
